import { DestroyRef, Injectable, inject } from '@angular/core';

import { NotificationService } from './notification.service';

/**
 * Реестр открытых окон подтверждения. Нужен, потому что подтверждений в проекте два вида:
 * глобальное (ConfirmationService + app-confirmation-dialog) и локальное
 * (app-confirmation-modal с @Input/@Output). Оба рисуются на одном слое (z-index 1100),
 * причём глобальное — всегда поверх, так как объявлено в app.html после router-outlet.
 * Второе подтверждение поверх первого означало бы, что пользователь читает один вопрос,
 * а отвечает на другой.
 *
 * Держится отдельно от ConfirmationService намеренно: так локальная модалка не зависит
 * от сервиса конкурирующего механизма, и спеки, подменяющие ConfirmationService моком,
 * не ломаются от того, что где-то в дереве отрисовалась app-confirmation-modal.
 *
 * BE-1237: кто попадает в реестр, решает вход `layer` у app-modal, а не то, каким
 * компонентом окно собрано. Контентные окна (дефолт) слой не трогают: поверх контентной
 * модалки подтверждение поднимать штатно — так работает рассылка уведомлений в карточке
 * розыгрыша.
 *
 * Это не OverlayCoordinatorService: у него противоположная политика — он закрывает
 * предыдущий оверлей при открытии нового, а здесь нужно отбить новый.
 *
 * Вторая обязанность — объяснить отбой пользователю (noticeBlocked/resetNotice). Текст и
 * дедуп живут здесь, а не у каждого механизма отдельно: отбить попытку может и сервис, и
 * окно, а тост на смешанную серию попыток обязан быть один.
 *
 * Владельцы слоя снимают его САМИ — возвращённой из acquire() функцией либо своим
 * DestroyRef. Массового снятия у реестра нет намеренно: реестр счётный, и «снять всё»
 * означало бы снять чужой слой (BE-1237).
 */
@Injectable({ providedIn: 'root' })
export class ConfirmationLockService {
  private readonly open = new Set<object>();
  private readonly notify = inject(NotificationService);

  /**
   * BE-1237: показывали ли уже подсказку про занятый слой. Дедуп живёт здесь, а не у
   * каждого механизма отдельно: отбить попытку может и глобальный ConfirmationService,
   * и локальное окно, а тост на смешанную серию попыток обязан быть один.
   */
  private noticeShown = false;

  /** Открыто ли сейчас какое-нибудь окно подтверждения. */
  get locked(): boolean {
    return this.open.size > 0;
  }

  /**
   * Занимает слой на время показа окна. Если передан `destroyRef` — снятие произойдёт
   * само при уничтожении владельца; иначе вызывать возвращённую функцию вручную.
   * Тот же контракт, что у `UnsavedChangesService.register`.
   */
  acquire(destroyRef?: DestroyRef): () => void {
    const token = {};
    this.open.add(token);

    const release = () => this.open.delete(token);
    destroyRef?.onDestroy(release);
    return release;
  }

  /**
   * Объясняет отбой вслух: иначе действие просто не происходит, и это выглядит как
   * сломанная кнопка. Подсказка одна на серию попыток — Enter по кнопке под оверлеем
   * даёт их пачкой.
   */
  noticeBlocked(): void {
    if (this.noticeShown) {
      return;
    }
    this.noticeShown = true;
    this.notify.showInfo(
      'Сначала завершите текущее действие',
      'Ответьте в открытом окне подтверждения и повторите.',
    );
  }

  /** Серия попыток закончилась (подтверждение открылось или закрылось) — объясняем снова. */
  resetNotice(): void {
    this.noticeShown = false;
  }
}
